import { FoundryLifecycle } from './foundry-lifecycle.js';
import { watchFoundryTabFit } from './foundry-tab-fit.js';
let FoundryTabs = {};


FoundryTabs = (function () {
    const VALID_TABS = new Set(['moon-fuse-panel', 'moon-dream-panel']);

    /* A TAB SWITCH HANDS OVER; IT NEVER LOADS IN FRONT OF THE PLAYER.
       The Foundry opens on one tab and has the other read and lay out its
       first view unseen (`data-foundry-preparing`, set and cleared by the
       controller that owns the panel — foundry.css keeps such a panel laid
       out but unpainted). A press that lands before that first view is whole
       used to show the tab anyway and let it finish in front of the player:
       round 13 (WebKit, iPhone 13 landscape, EU phone 4G, warm) caught the
       Moon Dream library appearing with an empty portrait disc and the face
       arriving 321 ms later. The owner (26 September 2026): loading is still
       chaotic, "switching from moon dream screen to moon fuse".

       So the tab on screen stays up, out of reach, until the tab being
       entered is whole, and the two swap in one task. The press is answered
       on the tab itself at once. A wait long enough to see earns ONE loading
       state, only past the shared art grace and only while the entered tab is
       really still waiting (`aria-busy` on the pressed tab and the panels,
       which foundry.css draws). And the hand-over is a readiness gate, so it
       is capped: at the doctrine's user-visible model (the 1500 ms mulligan
       reveal, docs/asset-loading.md law 2) the tab is shown with whatever is
       still loading in its own truthful state. A tab that is already whole —
       every switch after the first — swaps in the task of the press, exactly
       as before, and never starts a timer.

       PICTURES WITH THEIR OWN WELLS GET THE GRACE, NOT THE CAP. A tab whose
       reads are in and laid out and which waits only on pictures that draw
       their own loading well (`data-foundry-awaiting="pictures"`, set by its
       controller) is held for the grace alone: a face that lands inside it
       still makes a whole swap, and past it the tab is swapped in with those
       wells as its one loading state. Held for the cap instead, rows that were
       ready all along stayed behind the old tab for 1.5 s under the switch's
       loading state and then arrived with the wells anyway — two loading
       states in a row (26 September 2026 review, slow 4G). Anything a tab
       holds unseen until it settles (a staged deck) keeps the whole hand-over:
       it would otherwise pop in. Once the switch's loading state is showing,
       it is the one the player gets, and the tab is swapped in whole.

       THE PRESS ASKS FIRST. `moonfall:foundry-tabrequest` goes out in the
       task of every press that changes the tab, before the switch decides, so
       the entered tab's controller can start the reads its first frame needs
       and mark its panel preparing in that same task; the hand-over then
       covers them. Opened on Moon Dream, Moon Fuse lays out everything unseen
       and reads its canvas too, handing that workspace's lease straight back
       (the lease belongs to the tab on screen); the press is the player
       choosing the tab, and it projects that answer at once while its own
       open takes the lease (foundry.js peekCanvas). A hand-over taken back
       or closed sends `moonfall:foundry-tabrequestcancel`, so that
       controller can stand down; a swap sends the usual
       tabchanging/tabchange pair.

       INTENT COMES BEFORE THE PRESS. A hover, focus or touch on a tab that is
       not on screen sends `moonfall:foundry-tabintent`, so the entered tab's
       controller can read unseen what it does not prepare ahead of every open
       (Moon Fuse's inventory: every card the player owns; its canvas again
       when the unseen read of it has gone stale) and the press finds it in
       hand. Intent is only a read ahead: it changes nothing on screen and
       keeps nothing a press would (a canvas read hands its lease straight
       back; the press's own open takes it). */
    const SWITCH_LOADING_GRACE_MS = 450;
    const SWITCH_HOLD_CAP_MS = 1500;
    const PREPARING_ATTRIBUTE = 'data-foundry-preparing';
    const AWAITING_ATTRIBUTE = 'data-foundry-awaiting';

    function syncUrl(targetId) {
        const url = new URL(window.location.href);
        url.searchParams.set('surface', 'foundry');
        url.searchParams.set('tab', targetId === 'moon-dream-panel' ? 'moon-dream' : 'moon-fuse');
        if (url.hash) url.hash = '';
        window.history.replaceState({}, '', url.toString());
    }

    function mount(root, { initialTab = 'moon-fuse-panel' } = {}) {
        if (!root?.querySelectorAll) throw new Error('Foundry tabs require a surface root');
        const tabs = Array.from(root.querySelectorAll('.foundry-tab[data-target]'));
        const panels = new Map(
            Array.from(root.querySelectorAll('.foundry-panel')).map(panel => [panel.id, panel])
        );
        const contextualTools = Array.from(root.querySelectorAll('[data-foundry-tools-for]'));
        const tabList = root.querySelector('.foundry-tablist') || tabs[0]?.parentElement || null;
        const events = new AbortController();
        let activeTab = null;
        let fit = null;
        let pending = null;

        if (tabList) {
            tabList.setAttribute('role', 'tablist');
            if (!tabList.hasAttribute('aria-label') && !tabList.hasAttribute('aria-labelledby')) {
                tabList.setAttribute('aria-label', 'Moon Foundry');
            }
        }

        tabs.forEach((tab) => {
            const targetId = tab.dataset.target;
            const panel = panels.get(targetId);
            if (!tab.id) tab.id = `foundry-tab-${targetId.replace(/-panel$/, '')}`;
            tab.setAttribute('role', 'tab');
            tab.setAttribute('aria-controls', targetId);
            if (panel) {
                panel.setAttribute('role', 'tabpanel');
                panel.setAttribute('aria-labelledby', tab.id);
            }
        });

        /** The pressed tab's own look — the answer to the press. */
        function markSelection(targetId) {
            tabs.forEach((tab) => {
                const selected = tab.dataset.target === targetId;
                tab.classList.toggle('active', selected);
                tab.setAttribute('aria-selected', String(selected));
                tab.tabIndex = selected ? 0 : -1;
            });
        }

        function panelIsPreparing(targetId) {
            return panels.get(targetId)?.hasAttribute(PREPARING_ATTRIBUTE) === true;
        }

        /** Laid out, and waiting only on pictures that draw their own loading well. */
        function panelAwaitsOnlyPictures(targetId) {
            return panelIsPreparing(targetId)
                && panels.get(targetId)?.getAttribute(AWAITING_ATTRIBUTE) === 'pictures';
        }

        function dispatchTabEvent(type, detail) {
            window.dispatchEvent(new CustomEvent(type, { detail }));
        }

        function panelsContainer() {
            return panels.values().next().value?.parentElement || null;
        }

        function showSwitchLoading(current) {
            if (pending !== current) return;
            current.loading = true;
            tabs.forEach((tab) => {
                if (tab.dataset.target === current.targetId) tab.setAttribute('aria-busy', 'true');
            });
            panelsContainer()?.setAttribute('aria-busy', 'true');
        }

        /**
         * End a hand-over: its timers, its watch, its marks. `withdrawn` when
         * it ends without the swap (taken back, or the Foundry closed), which
         * the entered tab's controller is told.
         */
        function releasePending({ withdrawn = false } = {}) {
            const current = pending;
            if (!current) return;
            pending = null;
            clearTimeout(current.graceTimer);
            clearTimeout(current.capTimer);
            current.observer?.disconnect();
            if (current.loading) {
                tabs.forEach((tab) => tab.removeAttribute('aria-busy'));
                panelsContainer()?.removeAttribute('aria-busy');
            }
            if (current.leaving && !current.leavingWasInert) current.leaving.removeAttribute('inert');
            if (withdrawn) {
                dispatchTabEvent('moonfall:foundry-tabrequestcancel', {
                    previousTargetId: current.previousTargetId,
                    targetId: current.targetId,
                });
            }
        }

        /** The grace is over: pictures with their own wells go on in the tab; anything else earns the loading state. */
        function endSwitchGrace(current) {
            if (pending !== current) return;
            current.graceTimer = null;
            if (panelAwaitsOnlyPictures(current.targetId)) {
                commit(current.targetId);
                return;
            }
            showSwitchLoading(current);
        }

        /** Show `targetId`: everything the switch changes, in this one task. */
        function commit(targetId) {
            releasePending();
            const previousTargetId = activeTab;
            if (previousTargetId === targetId) {
                markSelection(targetId);
                return;
            }

            window.dispatchEvent(new CustomEvent('moonfall:foundry-tabchanging', {
                detail: { previousTargetId, targetId },
            }));
            markSelection(targetId);
            panels.forEach((panel, id) => {
                const selected = id === targetId;
                panel.classList.toggle('active', selected);
                panel.hidden = !selected;
            });
            contextualTools.forEach((tools) => {
                const selected = tools.dataset.foundryToolsFor === targetId;
                tools.hidden = !selected;
                tools.setAttribute('aria-hidden', String(!selected));
            });
            activeTab = targetId;
            /* Swapping panels swaps the contextual tools beside the tabs — a
               different price note, a different pill — and the fit already
               sizes the labels for EVERY tab's tools (foundry-tab-fit.js), so
               this measures the same scale unless a tool's own text changed
               while it was hidden. Inside the same task as the swap, before
               anything is painted. */
            fit?.refit?.();
            FoundryLifecycle?.setActiveTab?.(targetId);
            syncUrl(targetId);
            window.dispatchEvent(new CustomEvent('moonfall:foundry-tabchange', {
                detail: { previousTargetId, targetId },
            }));
        }

        /** Keep the tab on screen until `targetId` is whole, capped. */
        function handOver(targetId) {
            const panel = panels.get(targetId);
            const leaving = panels.get(activeTab) || null;
            markSelection(targetId);
            const current = {
                previousTargetId: activeTab,
                targetId,
                leaving,
                leavingWasInert: leaving?.hasAttribute('inert') === true,
                loading: false,
                observer: null,
                graceTimer: null,
                capTimer: null,
            };
            pending = current;
            // The tab on screen is leaving: nothing on it may be started now.
            leaving?.setAttribute('inert', '');
            if (typeof MutationObserver === 'function') {
                current.observer = new MutationObserver(() => {
                    if (pending === current && !panelIsPreparing(targetId)) commit(targetId);
                });
                current.observer.observe(panel, {
                    attributes: true,
                    attributeFilter: [PREPARING_ATTRIBUTE],
                });
            }
            current.graceTimer = setTimeout(() => endSwitchGrace(current), SWITCH_LOADING_GRACE_MS);
            current.capTimer = setTimeout(() => {
                if (pending === current) commit(targetId);
            }, SWITCH_HOLD_CAP_MS);
        }

        function activate(targetId) {
            if (!VALID_TABS.has(targetId) || !panels.has(targetId)) return false;
            if (pending) {
                // Pressed again while it is on its way: it is already coming.
                if (pending.targetId === targetId) return true;
                // Any other press takes the switch back before it happened.
                releasePending({ withdrawn: true });
            }
            if (activeTab === targetId) {
                markSelection(targetId);
                return true;
            }
            /* The opening tab is never held: the surface keeps itself unpainted
               until that tab's first view is in (FoundrySurface.presentWhenReady). */
            if (activeTab !== null) {
                dispatchTabEvent('moonfall:foundry-tabrequest', { previousTargetId: activeTab, targetId });
                if (panelIsPreparing(targetId)) {
                    handOver(targetId);
                    return true;
                }
            }
            commit(targetId);
            return true;
        }

        function moveSelection(event) {
            if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
            const currentIndex = tabs.indexOf(event.currentTarget);
            if (currentIndex < 0) return;

            let nextIndex;
            if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
            else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
            else if (event.key === 'Home') nextIndex = 0;
            else if (event.key === 'End') nextIndex = tabs.length - 1;
            else return;

            event.preventDefault();
            const nextTab = tabs[nextIndex];
            activate(nextTab.dataset.target);
            try {
                nextTab.focus({ preventScroll: true });
            } catch (_) {
                nextTab.focus();
            }
        }

        function signalIntent(targetId) {
            if (!VALID_TABS.has(targetId) || targetId === activeTab || pending?.targetId === targetId) return;
            dispatchTabEvent('moonfall:foundry-tabintent', { targetId });
        }

        tabs.forEach((tab) => {
            tab.addEventListener('click', () => activate(tab.dataset.target), {
                signal: events.signal,
            });
            tab.addEventListener('keydown', moveSelection, { signal: events.signal });
            ['pointerenter', 'pointerdown', 'focus'].forEach((type) => {
                tab.addEventListener(type, () => signalIntent(tab.dataset.target), { signal: events.signal });
            });
        });

        activate(VALID_TABS.has(initialTab) ? initialTab : 'moon-fuse-panel');
        /* After the first activate, so the very first measurement sees the
           tools the opening tab actually shows. */
        fit = watchFoundryTabFit(root, { signal: events.signal });

        return {
            activate,
            /* The surface calls this once the themed faces have activated: the
               labels are measured in Cinzel, and until it resolves the whole
               surface is `visibility: hidden`, so the refit lands before the
               player's first frame rather than after it. */
            refit() {
                return fit?.refit?.() ?? null;
            },
            /** The tab on screen; a tab still being handed over is not it yet. */
            getActiveTab() {
                return activeTab;
            },
            dispose() {
                releasePending({ withdrawn: true });
                events.abort();
            },
        };
    }

    return { mount };
})();

export { FoundryTabs };
